import { alpha, Box, Paper, Typography } from "@mui/material";
import { BlockchainAnalysisResponse } from "../../../services/agent.service";
import { AttachedFiles, CreatedBy, CrmProductInfo, InfoRow, ProductStatus, Section } from "./common";
import { ProductMetadata } from "./types";
import { format } from "date-fns";

const BlockchainAnalysisDetails: React.FC<{
  productInfo: BlockchainAnalysisResponse;
  productMetaData: ProductMetadata;
  onProductUpdate: (updatedProduct: BlockchainAnalysisResponse) => void;
}> = ({ productInfo, productMetaData, onProductUpdate }) => {
  const { createdBy, product } = productInfo;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <CrmProductInfo productCrm={productInfo.productCrm} />

      {/* Basic Info */}
      <Section title="Details">
        <InfoRow label="Created at" value={format(product.createdAt, "yyyy-MM-dd HH:mm")} />
        <InfoRow label="Product Request ID" value={product.productRequestId} />
      </Section>

      {/* Client Details */}
      <Section title="Client Details">
        <InfoRow label="Full Name" value={product.fullName} />
        <InfoRow label="Email" value={product.email} />
        <InfoRow label="User ID (CRM)" value={product.userId} />
      </Section>

      {/* Created By */}
      <CreatedBy createdBy={createdBy} />

      {/* Analysis Results */}
      {product.analysisResults && (
        <Section title="Analysis Results">
          <InfoRow label="Risk Score" value={product.analysisResults.riskScore} />
          <InfoRow label="Risk Level" value={product.analysisResults.riskLevel} />
          {product.analysisResults.transactionDetails && (
            <>
              <InfoRow label="From" value={product.analysisResults.transactionDetails.from} mono />
              <InfoRow label="To" value={product.analysisResults.transactionDetails.to} mono />
              <InfoRow label="Amount" value={product.analysisResults.transactionDetails.amount} />
              <InfoRow label="Entity" value={product.analysisResults.transactionDetails.entity} />
            </>
          )}
          {product.analysisError && (
            <Paper sx={{ p: 2, bgcolor: alpha("#f44336", 0.1), border: `1px solid ${alpha("#f44336", 0.2)}`, borderRadius: 1.5, overflowX: "auto" }}>
              <Typography variant="subtitle2" sx={{ color: "#d32f2f", mb: 1, fontSize: "0.85rem" }}>
                Analysis Error
              </Typography>
              <Typography variant="caption" sx={{ color: "#d32f2f" }}>
                {product.analysisError}
              </Typography>
            </Paper>
          )}
        </Section>
      )}

      {/* Form Data */}
      {product.formData && Object.keys(product.formData).length > 0 && (
        <Section title="Form Data">
          {Object.entries(product.formData).map(([key, value]) => (
            <InfoRow key={key} label={key.charAt(0).toUpperCase() + key.slice(1)} value={value} />
          ))}
        </Section>
      )}

      {/* Attached Files */}
      <AttachedFiles attachments={productInfo.attachments} productMetaData={productMetaData} />

      {/* Process Status */}
      <ProductStatus productInfo={productInfo} productMetaData={productMetaData} onProductUpdate={onProductUpdate} />
    </Box>
  );
};

export default BlockchainAnalysisDetails;
